import {
    useChangeOwnPassword,
    useOrgAuth,
    useOrgAuthStore,
    usePatchMember
} from "@solomon-apps/shared";
import { ContentBlock } from "@solomon-apps/shared/src/components/console";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    CheckIcon,
    CrossIcon,
    PencilPaperIcon
} from "@solomon-labs/design/components/icons";
import {
    ButtonIcon,
    Card,
    CardContent
} from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import { LabeledInput, ReadOnlyField } from "@/pages/shared/LabeledField";
import {
    OUTLINE_GROUP_CLASS,
    READ_ONLY_GROUP_CLASS
} from "@/pages/shared/lightbarField";
import { resolveClientFormError } from "@/utils/clientFormErrors";

export function SettingsPage(): React.ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const setMe = useOrgAuthStore((s) => s.setMe);
    const { logout } = useOrgAuth(CLIENT_OAUTH_CONFIG);

    return (
        <div className="flex flex-col pt-3 md:pt-6 w-full min-h-full">
            <Heading
                asElement="header"
                title="Profile"
                className="px-4 md:px-6 pb-3 md:pb-6 border-b border-line-base"
            />

            {/* Two equal columns on wide screens: the read-mostly Account
                details on the left (closed by a full-height rule), and the one
                editable form, Change Password, on the right in a sunken,
                outlined card so it reads as a form. Below lg they stack,
                Account first. */}
            {me && (
                <div className="flex w-full flex-1 flex-col lg:flex-row">
                    <div className="flex w-full flex-col py-3 md:py-6 lg:w-1/2 lg:border-r lg:border-line-base">
                        <AccountInfoSection
                            uuid={me.user.uuid}
                            fullName={me.user.fullName}
                            email={me.user.email}
                            orgRole={me.orgRole}
                            onNameUpdated={(name) =>
                                setMe({
                                    ...me,
                                    user: { ...me.user, fullName: name }
                                })
                            }
                        />
                    </div>
                    <div className="w-full px-4 py-3 md:px-6 md:py-6 lg:w-1/2">
                        <ChangePasswordCard onSuccess={() => void logout()} />
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Account info (read-only, on surface-ground) ──────────────────────

interface AccountInfoSectionProps {
    uuid: string;
    fullName: string | null;
    email: string;
    orgRole: string;
    onNameUpdated: (name: string) => void;
}

function AccountInfoSection({
    uuid,
    fullName,
    email,
    orgRole,
    onNameUpdated
}: AccountInfoSectionProps): React.ReactElement {
    return (
        <ContentBlock title="Account" className="px-4 md:px-6 gap-1 md:gap-2">
            <div className="flex flex-col gap-3 py-4">
                <EditableNameRow
                    uuid={uuid}
                    fullName={fullName}
                    onNameUpdated={onNameUpdated}
                />
                <ReadOnlyField label="Email" value={email} />
                <ReadOnlyField label="Role" value={orgRole} />
            </div>
        </ContentBlock>
    );
}

// ── Editable name row (inline edit inside the Account section) ───────

function EditableNameRow({
    uuid,
    fullName,
    onNameUpdated
}: {
    uuid: string;
    fullName: string | null;
    onNameUpdated: (name: string) => void;
}): React.ReactElement {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(fullName ?? "");
    const { patchMemberAsync, isPatching, error } =
        usePatchMember(CLIENT_OAUTH_CONFIG);

    const handleSave = async (): Promise<void> => {
        const trimmed = name.trim();
        if (!trimmed) return;
        try {
            await patchMemberAsync({ uuid, data: { fullName: trimmed } });
            onNameUpdated(trimmed);
            setEditing(false);
        } catch {
            // surfaced via the hook's error state below
        }
    };

    const cancel = (): void => {
        setName(fullName ?? "");
        setEditing(false);
    };

    return (
        <div className="flex flex-col gap-1">
            {editing ? (
                <LabeledInput
                    label="Name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            void handleSave();
                        } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancel();
                        }
                    }}
                    disabled={isPatching}
                    aria-invalid={error ? true : undefined}
                    aria-label="Name"
                    autoFocus
                    actions={
                        <>
                            <ButtonIcon
                                variant="transparent"
                                size="lg"
                                icon={<CheckIcon />}
                                aria-label="Save name"
                                disabled={isPatching || !name.trim()}
                                onClick={() => void handleSave()}
                            />
                            <ButtonIcon
                                variant="transparent"
                                size="lg"
                                icon={<CrossIcon />}
                                aria-label="Cancel editing name"
                                disabled={isPatching}
                                onClick={cancel}
                            />
                        </>
                    }
                />
            ) : (
                <LabeledInput
                    label="Name"
                    groupClassName={READ_ONLY_GROUP_CLASS}
                    type="text"
                    value={fullName ?? ""}
                    readOnly
                    aria-label="Name"
                    actions={
                        <ButtonIcon
                            variant="transparent"
                            size="lg"
                            icon={<PencilPaperIcon />}
                            aria-label="Edit name"
                            onClick={() => setEditing(true)}
                        />
                    }
                />
            )}
            {editing && error ? (
                <Text variant="small" className="text-error-base">
                    {resolveClientFormError(
                        error,
                        "settings",
                        "Name was not updated. Check your access and try again."
                    )}
                </Text>
            ) : null}
        </div>
    );
}

// ── Change Password (editable, flat section with divider rhythm) ─────

function ChangePasswordCard({
    onSuccess
}: {
    onSuccess: () => void;
}): React.ReactElement {
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { changePasswordAsync, isChanging, error } =
        useChangeOwnPassword(CLIENT_OAUTH_CONFIG);

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setValidationError(null);
        setSubmitError(null);

        if (newPassword.length < 8 || newPassword.length > 72) {
            setValidationError("Password must be between 8 and 72 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setValidationError("Passwords do not match.");
            return;
        }

        try {
            await changePasswordAsync({ oldPassword, newPassword });
            onSuccess();
        } catch (caught) {
            setSubmitError(
                resolveClientFormError(
                    caught,
                    "settings",
                    "Password was not changed. Check the current password and try again."
                )
            );
        }
    };

    const renderedError =
        validationError ??
        submitError ??
        (error
            ? resolveClientFormError(
                  error,
                  "settings",
                  "Password was not changed. Check the current password and try again."
              )
            : null);

    return (
        <Card surface="sunken" border="line">
            <CardContent className="pt-6">
                <ContentBlock
                    title="Change Password"
                    className="gap-1 md:gap-2"
                >
                    <form
                        onSubmit={(e) => void handleSubmit(e)}
                        className="flex flex-col"
                    >
                        <div className="py-4 flex flex-col gap-3">
                            <LabeledInput
                                label="Current"
                                groupClassName={OUTLINE_GROUP_CLASS}
                                type="password"
                                autoComplete="current-password"
                                required
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                disabled={isChanging}
                            />
                            <LabeledInput
                                label="New"
                                groupClassName={OUTLINE_GROUP_CLASS}
                                type="password"
                                autoComplete="new-password"
                                required
                                minLength={8}
                                maxLength={72}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isChanging}
                            />
                            <LabeledInput
                                label="Confirm New"
                                groupClassName={OUTLINE_GROUP_CLASS}
                                type="password"
                                autoComplete="new-password"
                                required
                                minLength={8}
                                maxLength={72}
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                disabled={isChanging}
                            />

                            {renderedError && (
                                <Text
                                    variant="small"
                                    className="text-error-base"
                                >
                                    {renderedError}
                                </Text>
                            )}
                        </div>

                        <div className="py-3 flex items-center justify-start">
                            <Button
                                variant="raised"
                                size="md"
                                interaction="lifted"
                                type="submit"
                                disabled={isChanging}
                            >
                                {isChanging ? "Changing..." : "Change Password"}
                            </Button>
                        </div>
                    </form>
                </ContentBlock>
            </CardContent>
        </Card>
    );
}
